import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-final-form'

import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import {
  Add,
  Clipboard,
  CloseSmall,
  Email,
  File as FileIcon,
  ImageIcon,
} from 'common/icons'

import ImportContentModal, { ReferenceSource } from './ImportContentModal'

type GenerateBriefFromContentOptions = {
  brandVoiceId?: string
  mergeTagIds?: string[]
}

type Props = {
  fieldName: string
  isDisabled?: boolean
  isGenerating: boolean
  generatedValue?: string
  rawContent?: string
  rawContentType?: 'html' | 'text' | 'image'
  showPasteHtml: boolean
  showUploadPdf: boolean
  showUploadImage: boolean
  showUploadEml: boolean
  onGenerateBriefFromContent: (
    content: string,
    contentType: 'html' | 'text' | 'image',
    options?: GenerateBriefFromContentOptions
  ) => void
  onRemoveRawContent: () => void
}

const sourceFromRawContentType = (
  rawContentType?: 'html' | 'text' | 'image'
): ReferenceSource | null => {
  if (rawContentType === 'text') {
    return 'pdf'
  }
  if (rawContentType === 'image') {
    return 'image'
  }
  if (rawContentType === 'html') {
    return 'html'
  }
  return null
}

const chipLabelForSource = (source: ReferenceSource): string => {
  if (source === 'pdf') {
    return 'PDF reference'
  }
  if (source === 'image') {
    return 'Image reference'
  }
  if (source === 'eml') {
    return 'Email reference'
  }
  return 'HTML reference'
}

const chipIconForSource = (source: ReferenceSource) => {
  if (source === 'pdf') {
    return FileIcon
  }
  if (source === 'image') {
    return ImageIcon
  }
  if (source === 'eml') {
    return Email
  }
  return Clipboard
}

const BriefContentImportActions = ({
  fieldName,
  isDisabled,
  isGenerating,
  generatedValue,
  rawContent,
  rawContentType,
  showPasteHtml,
  showUploadPdf,
  showUploadImage,
  showUploadEml,
  onGenerateBriefFromContent,
  onRemoveRawContent,
}: Props) => {
  const form = useForm()
  const hasRawSource = !!rawContent

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [referenceSource, setReferenceSource] =
    useState<ReferenceSource | null>(() =>
      sourceFromRawContentType(rawContentType)
    )
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false)
  const isAwaitingResultRef = useRef(false)
  const previousGeneratedValueRef = useRef(generatedValue)
  const preImportValueRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (
      isAwaitingResultRef.current &&
      generatedValue &&
      generatedValue !== previousGeneratedValueRef.current
    ) {
      form.initialize((values) => ({
        ...values,
        [fieldName]: generatedValue,
      }))
      isAwaitingResultRef.current = false
    }
    previousGeneratedValueRef.current = generatedValue
  }, [generatedValue, fieldName, form])

  useEffect(() => {
    if (!hasRawSource) {
      setReferenceSource(null)
      return
    }
    setReferenceSource(
      (current) => current ?? sourceFromRawContentType(rawContentType)
    )
  }, [hasRawSource, rawContentType])

  const handleImportContent = (
    content: string,
    contentType: 'html' | 'text' | 'image',
    source: ReferenceSource
  ) => {
    // Only capture the pre-import baseline on the *first* import — if the
    // user replaces an already-imported source without removing it first,
    // removal should still revert to the original value, not the
    // intermediate one from the previous import.
    if (!hasRawSource) {
      preImportValueRef.current = generatedValue
    }
    isAwaitingResultRef.current = true
    setReferenceSource(source)
    setIsModalOpen(false)

    const { values } = form.getState()
    onGenerateBriefFromContent(content, contentType, {
      brandVoiceId: (values.brand_voice as { value?: string } | undefined)
        ?.value,
      mergeTagIds: values.merge_tags as string[] | undefined,
    })
  }

  const handleRemoveRawContent = () => {
    // form.initialize (not form.change) so this reverts to a fresh pristine
    // baseline instead of marking the field dirty — a dirty field here would
    // flip BriefPage's state machine into the old "formChangedWithBrief"
    // state, which predates (and hides) the import-flow's Focus Themes /
    // Personalisation Tags bypass.
    form.initialize((values) => ({
      ...values,
      [fieldName]: preImportValueRef.current ?? '',
    }))
    onRemoveRawContent()
    setReferenceSource(null)
    setIsRemoveConfirmOpen(false)
  }

  const chipSource = referenceSource ?? sourceFromRawContentType(rawContentType)
  const chipLabel = chipSource ? chipLabelForSource(chipSource) : ''
  const ChipIcon = chipSource ? chipIconForSource(chipSource) : Clipboard

  return (
    <div className="pt-2">
      <div className="flex items-center gap-2">
        {!hasRawSource && (
          <button
            type="button"
            disabled={isDisabled || isGenerating}
            onClick={() => setIsModalOpen(true)}
            data-testid="brief-content-add-reference-button"
            className="flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-gold-400 hover:border-base-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium text-base-700"
          >
            <Add size={4} isDefaultColor={false} />
            Add reference
          </button>
        )}
        {hasRawSource && chipSource && (
          <span
            className="flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full bg-maroon-40 text-maroon-500 border border-maroon-400"
            data-testid="raw-content-chip"
          >
            <ChipIcon size={4} isDefaultColor={false} />
            {chipLabel}
            <button
              type="button"
              onClick={() => setIsRemoveConfirmOpen(true)}
              disabled={isDisabled || isGenerating}
              data-testid="raw-content-remove-button"
            >
              <CloseSmall size={3} isDefaultColor={false} />
            </button>
          </span>
        )}
      </div>

      {isGenerating && (
        <p className="text-xs text-base-500 pt-1">
          Jacquard is summarising. May take a moment...
        </p>
      )}

      <ImportContentModal
        isOpen={isModalOpen}
        currentDescription={generatedValue}
        enabledTypes={{
          html: showPasteHtml,
          pdf: showUploadPdf,
          image: showUploadImage,
          eml: showUploadEml,
        }}
        onClose={() => setIsModalOpen(false)}
        onImportHtml={(html) => handleImportContent(html, 'html', 'html')}
        onImportPdf={(text) => handleImportContent(text, 'text', 'pdf')}
        onImportImage={(dataUri) =>
          handleImportContent(dataUri, 'image', 'image')
        }
        // The .eml path extracts the email's HTML body client-side — from
        // content-api's point of view this is indistinguishable from a
        // pasted-HTML import, so it reuses the same 'html' content_type
        // rather than needing any new backend content_type value.
        onImportEml={(html) => handleImportContent(html, 'html', 'eml')}
      />

      <ConfirmationModal
        open={isRemoveConfirmOpen}
        title="Remove reference"
        confirmationText="Removing this will reset the brief description to what it was before you added the reference. Please note this change before proceeding."
        confirmButtonText="Proceed"
        cancelButtonText="Cancel"
        confirmButtonVariant="primary"
        onCancel={() => setIsRemoveConfirmOpen(false)}
        onConfirm={handleRemoveRawContent}
      />
    </div>
  )
}

export default BriefContentImportActions
